import React, { useState } from "react";
import { Form, Button, Upload, message, Result } from "antd";
import { UploadOutlined, CheckCircleFilled, FileTextOutlined, DeleteOutlined } from "@ant-design/icons";

import { submitVendorOnboardingApi, uploadImageApi } from "../../services/api";
import AppInput from "../../common/FormInputs/AppInput";

const ACCENT = "#0f6e56";
const MAX_FILE_MB = 5;
// Standard 15-character GSTIN format (2-digit state code, 10-char PAN, entity
// code, default "Z", checksum) — catches obviously-wrong input before it
// reaches a Super Admin for review.
const GSTIN_PATTERN = /^\d{2}[A-Z]{5}\d{4}[A-Z]\d[A-Z]\d[A-Z][A-Z\d]$/;

function DocUploadBox({ label, hint, fileName, uploading, onUpload, onRemove }) {
  if (fileName) {
    return (
      <div
        style={{
          border: "1px solid #E5E7EB", borderRadius: 10, padding: "12px 16px",
          display: "flex", alignItems: "center", gap: 12, background: "#F9FAFB",
        }}
      >
        <div style={{ width: 36, height: 36, borderRadius: 8, background: "#E8F5E9", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <FileTextOutlined style={{ color: "#43A047", fontSize: 16 }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{label}</div>
          <div style={{ fontSize: 11, color: "#43A047", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            ✓ {fileName}
          </div>
        </div>
        <Button type="text" danger icon={<DeleteOutlined />} onClick={onRemove} size="small" />
      </div>
    );
  }

  return (
    <Upload.Dragger
      accept="image/*,.pdf"
      showUploadList={false}
      disabled={uploading}
      beforeUpload={(f) => { onUpload(f); return false; }}
      style={{ background: uploading ? "#F5F3FF" : "#FAFAFA", borderRadius: 10, borderColor: uploading ? ACCENT : undefined }}
    >
      <div style={{ padding: "14px 0" }}>
        <UploadOutlined style={{ fontSize: 22, color: uploading ? ACCENT : "#9CA3AF" }} spin={uploading} />
        <div style={{ fontSize: 13, fontWeight: 600, marginTop: 8, color: "#111827" }}>
          {uploading ? "Uploading…" : label}
        </div>
        <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{hint}</div>
      </div>
    </Upload.Dragger>
  );
}

export default function VendorOnboarding() {
  const [form] = Form.useForm();
  const [panUrl, setPanUrl] = useState("");
  const [panName, setPanName] = useState("");
  const [gstUrl, setGstUrl] = useState("");
  const [gstName, setGstName] = useState("");
  const [uploadingPan, setUploadingPan] = useState(false);
  const [uploadingGst, setUploadingGst] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleUpload = async (file, which) => {
    if (file.size / 1024 / 1024 > MAX_FILE_MB) {
      message.error(`File must be under ${MAX_FILE_MB}MB`);
      return;
    }
    which === "pan" ? setUploadingPan(true) : setUploadingGst(true);
    try {
      const res = await uploadImageApi(file, "vendor-documents");
      const url = res?.data?.data;
      if (url) {
        if (which === "pan") { setPanUrl(url); setPanName(file.name); }
        else { setGstUrl(url); setGstName(file.name); }
      } else {
        message.error("Upload failed — no file URL returned");
      }
    } catch {
      message.error("Document upload failed");
    } finally {
      which === "pan" ? setUploadingPan(false) : setUploadingGst(false);
    }
  };

  const handleRemove = (which) => {
    if (which === "pan") { setPanUrl(""); setPanName(""); }
    else { setGstUrl(""); setGstName(""); }
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    if (!panUrl) { message.error("Please upload the PAN document"); return; }
    if (!gstUrl) { message.error("Please upload the GST certificate"); return; }

    setSubmitting(true);
    try {
      const address = [
        values.addressLine1,
        values.addressLine2,
        [values.city, values.state].filter(Boolean).join(", "),
        values.pincode,
      ].filter(Boolean).join(", ");

      await submitVendorOnboardingApi({
        name: values.name,
        gstNumber: values.gstNumber.toUpperCase(),
        address,
        loginEmail: values.loginEmail,
        panDocumentUrl: panUrl,
        gstDocumentUrl: gstUrl,
      });
      setSubmitted(true);
    } catch (err) {
      message.error(err?.response?.data?.data || "Failed to submit vendor for review");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitAnother = () => {
    form.resetFields();
    handleRemove("pan");
    handleRemove("gst");
    setSubmitted(false);
  };

  if (submitted) {
    return (
      <Result
        icon={<CheckCircleFilled style={{ color: "#43A047" }} />}
        title="Vendor submitted for review"
        subTitle="A Super Admin will review the PAN/GST documents and approve or reject this vendor. They'll be emailed to set their password once approved."
        extra={
          <Button type="primary" style={{ background: ACCENT, border: "none", borderRadius: 8 }} onClick={handleSubmitAnother}>
            Submit Another Vendor
          </Button>
        }
      />
    );
  }

  return (
    <div style={{ width: "100%" }}>
      <div className="page-header">
        <div>
          <div className="page-title">Vendor Onboarding</div>
          <div className="page-sub">Submit a new vendor with PAN/GST for Super Admin review</div>
        </div>
      </div>

      <Form form={form} layout="vertical">
        {/* VENDOR DETAILS */}
        <div style={{ background: "#fff", padding: 24, borderRadius: 12, border: "1px solid #F0F0F0", marginBottom: 16 }}>
          <div className="section-title">Vendor Details</div>

          <div style={{ display: "flex", gap: 16 }}>
            <div style={{ flex: 1 }}>
              <AppInput name="name" label="Vendor / Business Name" rules={[{ required: true, message: "Vendor name is required" }]} />
            </div>
            <div style={{ flex: 1 }}>
              <AppInput
                name="gstNumber"
                label="GST Number (GSTIN)"
                placeholder="e.g. 27AAPFU0939F1ZV"
                rules={[
                  { required: true, message: "GST number is required" },
                  { pattern: GSTIN_PATTERN, message: "Enter a valid 15-character GSTIN" },
                ]}
                onChange={(e) => form.setFieldValue("gstNumber", e.target.value.toUpperCase())}
              />
            </div>
          </div>

          <AppInput
            name="loginEmail"
            label="Vendor's Login Email"
            rules={[{ required: true, message: "Login email is required" }, { type: "email", message: "Enter a valid email" }]}
            placeholder="vendor@example.com"
          />

          <div style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", letterSpacing: "0.4px", margin: "8px 0 4px" }}>
            BUSINESS ADDRESS
          </div>

          <AppInput name="addressLine1" label="Address Line 1" placeholder="Building, street" rules={[{ required: true, message: "Address line 1 is required" }]} />
          <AppInput name="addressLine2" label="Address Line 2 (optional)" placeholder="Locality, landmark" />

          <div style={{ display: "flex", gap: 16 }}>
            <div style={{ flex: 1 }}>
              <AppInput name="city" label="City" rules={[{ required: true, message: "City is required" }]} />
            </div>
            <div style={{ flex: 1 }}>
              <AppInput name="state" label="State" rules={[{ required: true, message: "State is required" }]} />
            </div>
            <div style={{ flex: 1 }}>
              <AppInput
                name="pincode"
                label="Pincode"
                rules={[
                  { required: true, message: "Pincode is required" },
                  { pattern: /^\d{6}$/, message: "Enter a valid 6-digit pincode" },
                ]}
              />
            </div>
          </div>
        </div>

        {/* DOCUMENTS */}
        <div style={{ background: "#fff", padding: 24, borderRadius: 12, border: "1px solid #F0F0F0", marginBottom: 16 }}>
          <div className="section-title">Documents</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <DocUploadBox
              label="PAN Document"
              hint="Image or PDF, up to 5MB"
              fileName={panName}
              uploading={uploadingPan}
              onUpload={(f) => handleUpload(f, "pan")}
              onRemove={() => handleRemove("pan")}
            />
            <DocUploadBox
              label="GST Certificate"
              hint="Image or PDF, up to 5MB"
              fileName={gstName}
              uploading={uploadingGst}
              onUpload={(f) => handleUpload(f, "gst")}
              onRemove={() => handleRemove("gst")}
            />
          </div>
        </div>

        <Button
          type="primary"
          block
          size="large"
          loading={submitting}
          style={{ background: ACCENT, border: "none", borderRadius: 8 }}
          onClick={handleSubmit}
        >
          Submit for Review
        </Button>
      </Form>
    </div>
  );
}
